import React from 'react';
import SaveHeartButton from '../../components/SaveHeartButton';

const decodeHtmlEntities = (value) => {
  if (!value) {
    return '';
  }

  if (typeof document === 'undefined') {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
};

const formatPublishTime = (publishTime) => {
  if (!publishTime) {
    return '';
  }

  const publishedDate = new Date(publishTime);
  if (Number.isNaN(publishedDate.getTime())) {
    return '';
  }

  const now = new Date();
  const diffMs = now.getTime() - publishedDate.getTime();
  if (diffMs <= 0) {
    return '1 hour ago';
  }

  const hourMs = 1000 * 60 * 60;
  const dayMs = hourMs * 24;
  const weekMs = dayMs * 7;
  const monthMs = dayMs * 30;
  const yearMs = dayMs * 365;

  const intervals = [
    { label: 'year', ms: yearMs },
    { label: 'month', ms: monthMs },
    { label: 'week', ms: weekMs },
    { label: 'day', ms: dayMs },
    { label: 'hour', ms: hourMs },
  ];

  for (const { label, ms } of intervals) {
    if (diffMs >= ms) {
      const count = Math.floor(diffMs / ms);
      const suffix = count === 1 ? '' : 's';
      return `${count} ${label}${suffix} ago`;
    }
  }

  return '1 hour ago';
};

const YouTubeVideoCard = ({ video, onSelect, onChannelSelect, isSaved = false, onSaveToggle }) => {
  if (!video) {
    return null;
  }

  const videoId = video.id?.videoId;
  const snippet = video.snippet ?? {};
  const title = snippet.title ?? '';
  const channelTitle = snippet.channelTitle ?? '';
  const channelId = snippet.channelId ?? '';
  const publishTime = snippet.publishTime ?? '';
  const publishedAgo = formatPublishTime(publishTime);
  const thumbnailUrl =
    snippet.thumbnails?.high?.url ||
    snippet.thumbnails?.medium?.url ||
    snippet.thumbnails?.default?.url ||
    '';

  const decodedTitle = decodeHtmlEntities(title);
  const decodedChannelTitle = decodeHtmlEntities(channelTitle);

  const handleClick = () => {
    if (videoId) {
      onSelect(video);
    }
  };

  const handleChannelClick = () => {
    if (channelId && onChannelSelect) {
      onChannelSelect(channelId);
    }
  };

  const handleSaveClick = () => {
    if (onSaveToggle) {
      onSaveToggle(video);
    }
  };

  return (
    <div className="relative flex flex-col border rounded-2xl p-3 bg-white shadow-sm text-left">
      <div
        className="relative aspect-video rounded-xl overflow-hidden flex items-center justify-center bg-black cursor-pointer"
        onClick={handleClick}
      >
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={decodedTitle} className="w-full h-full object-cover" />
        ) : (
          <div className="text-white">No thumbnail available</div>
        )}
      </div>
      <div className="flex-1 mt-4 space-y-2 pr-10">
        <h4 className="font-bold cursor-pointer text-sm" onClick={handleClick}>
          {decodedTitle}
        </h4>
        <p
          className="text-xs text-gray-600 cursor-pointer"
          onClick={handleChannelClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleChannelClick();
            }
          }}
        >
          {decodedChannelTitle}
        </p>
        {publishedAgo ? (
          <p className="text-xs text-gray-500">{publishedAgo}</p>
        ) : null}
      </div>
      {onSaveToggle && (
        <div className="absolute bottom-0 right-0 mb-2 mr-2 flex gap-1">
          <SaveHeartButton isSaved={isSaved} onClick={handleSaveClick} />
        </div>
      )}
    </div>
  );
};

export default YouTubeVideoCard;
